import React, { FC, useCallback } from 'react';
import { Stack, styled, Switch, type SxProps, type Theme, Typography } from '@mui/material';

import withFormControl from '@/hoc/withFormControl';
import { transforms } from '@/theme/constants';

interface ToggleSwitchProps {
  id?: string;
  name?: string;
  value?: boolean;
  onChange?: (event: boolean) => void;
  sx?: SxProps<Theme>;
  label?: string;
}

const SWITCH_HEIGHT = 24;
const SWITCH_WIDTH = 40;

const StyledStack = styled(Stack)({
  cursor: 'pointer',
  gap: 12
});

const StyledTypography = styled(Typography)(({ theme }) => ({
  ...theme.typography.body3,
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.grey[700],
  userSelect: 'none'
}));

const StyledSwitch = styled(Switch, {
  shouldForwardProp: prop => prop !== 'value'
})<{ value?: boolean }>(({ theme, value }) => ({
  '&.MuiSwitch-root': {
    height: SWITCH_HEIGHT,
    width: SWITCH_WIDTH,
    padding: theme.spacing(0.625),
    marginInline: theme.spacing(-0.625)
  },
  '& .MuiSwitch-thumb': {
    width: 10,
    height: 10
  },
  '& .MuiSwitch-switchBase': {
    padding: theme.spacing(0.875)
  },
  '& .MuiSwitch-switchBase.Mui-checked': {
    transform: transforms.shiftX(16)
  },
  '& .MuiSwitch-switchBase .MuiSwitch-thumb': {
    backgroundColor: value ? theme.palette.background.default : theme.palette.grey[800]
  },
  '&.MuiSwitch-root .MuiSwitch-track': {
    backgroundColor: value ? theme.palette.primary[400] : theme.palette.grey[400],
    opacity: 1
  }
}));

const ToggleSwitchField: FC<ToggleSwitchProps> = ({ id, value, name, onChange, label, sx }) => {
  const onToggle = useCallback(() => {
    onChange?.(!value);
  }, [onChange, value]);

  return (
    <StyledStack direction="row" alignItems="center" onClick={onToggle} sx={sx}>
      <StyledTypography>{label}</StyledTypography>
      <StyledSwitch id={id} name={name} checked={value} onChange={onToggle} value={value} />
    </StyledStack>
  );
};

const ToggleSwitch = withFormControl<boolean, boolean, ToggleSwitchProps>(ToggleSwitchField);

export default ToggleSwitch;
